// =============================================================================
// SIREPPAR - Componente Formulario en Pasos
// =============================================================================

import { useState, ReactNode, createContext, useContext } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';

// =============================================================================
// Tipos
// =============================================================================

interface Step {
  id: string;
  title: string;
  description?: string;
  component: ReactNode;
  validation?: () => Promise<boolean> | boolean;
}

interface StepFormProps {
  steps: Step[];
  onComplete: (data: Record<string, any>) => void;
  onCancel?: () => void;
  initialData?: Record<string, any>;
}

interface StepFormContextType {
  data: Record<string, any>;
  updateData: (stepId: string, stepData: any) => void;
  currentStep: number;
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const StepFormContext = createContext<StepFormContextType | null>(null);

export const useStepForm = () => {
  const context = useContext(StepFormContext);
  if (!context) {
    throw new Error('useStepForm debe usarse dentro de StepForm');
  }
  return context;
};

// =============================================================================
// Componente Principal
// =============================================================================

export default function StepForm({
  steps,
  onComplete,
  onCancel,
  initialData = {},
}: StepFormProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState<Record<string, any>>(initialData);
  const [isValidating, setIsValidating] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const updateData = (stepId: string, stepData: any) => {
    setData(prev => ({
      ...prev,
      [stepId]: { ...prev[stepId], ...stepData },
    }));
  };

  const goToStep = (step: number) => {
    if (step >= 0 && step < steps.length) {
      setCurrentStep(step);
    }
  };

  const nextStep = async () => {
    const step = steps[currentStep];
    
    if (step.validation) {
      setIsValidating(true);
      try {
        const isValid = await step.validation();
        if (!isValid) {
          setIsValidating(false);
          return;
        }
      } catch {
        setIsValidating(false);
        return;
      }
      setIsValidating(false);
    }

    setCompletedSteps(prev => new Set([...prev, currentStep]));

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(data);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const contextValue: StepFormContextType = {
    data,
    updateData,
    currentStep,
    goToStep,
    nextStep,
    prevStep,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === steps.length - 1,
  };

  return (
    <StepFormContext.Provider value={contextValue}>
      <div className="min-h-[500px] flex flex-col">
        {/* Indicador de pasos */}
        <nav aria-label="Progress" className="mb-8">
          <ol className="flex items-center">
            {steps.map((step, index) => (
              <li key={step.id} className={`relative ${index !== steps.length - 1 ? 'pr-8 sm:pr-20 flex-1' : ''}`}>
                {/* Línea conectora */}
                {index !== steps.length - 1 && (
                  <div className="absolute top-4 left-7 -right-3 sm:left-9 sm:-right-11">
                    <div className={`h-0.5 w-full ${
                      completedSteps.has(index) ? 'bg-primary-600' : 'bg-gray-200'
                    }`} />
                  </div>
                )}
                
                <div className="relative flex items-start group">
                  <span className="flex h-9 items-center">
                    <span className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full ${
                      completedSteps.has(index)
                        ? 'bg-primary-600'
                        : index === currentStep
                        ? 'border-2 border-primary-600 bg-white'
                        : 'border-2 border-gray-300 bg-white'
                    }`}>
                      {completedSteps.has(index) ? (
                        <CheckIcon className="h-5 w-5 text-white" />
                      ) : (
                        <span className={`${
                          index === currentStep ? 'text-primary-600' : 'text-gray-500'
                        }`}>
                          {index + 1}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="ml-4 min-w-0 flex flex-col">
                    <span className={`text-sm font-medium ${
                      index === currentStep ? 'text-primary-600' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </span>
                    {step.description && (
                      <span className="text-sm text-gray-500 hidden sm:block">
                        {step.description}
                      </span>
                    )}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </nav>

        {/* Contenido del paso actual */}
        <div className="flex-1 bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {steps[currentStep].title}
          </h2>
          {steps[currentStep].component}
        </div>

        {/* Botones de navegación */}
        <div className="mt-6 flex justify-between">
          <div>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Cancelar
              </button>
            )}
          </div>
          
          <div className="flex gap-3">
            {!contextValue.isFirstStep && (
              <button
                type="button"
                onClick={prevStep}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Anterior
              </button>
            )}
            
            <button
              type="button"
              onClick={nextStep}
              disabled={isValidating}
              className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {isValidating ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Validando...
                </span>
              ) : contextValue.isLastStep ? (
                'Finalizar'
              ) : (
                'Siguiente'
              )}
            </button>
          </div>
        </div>
      </div>
    </StepFormContext.Provider>
  );
}

// =============================================================================
// Componente de Resumen
// =============================================================================

interface StepSummaryProps {
  title: string;
  items: Array<{ label: string; value: string | ReactNode }>;
  onEdit?: () => void;
}

export function StepSummary({ title, items, onEdit }: StepSummaryProps) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-4 py-3 flex justify-between items-center">
        <h4 className="text-sm font-medium text-gray-900">{title}</h4>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Editar
          </button>
        )}
      </div>
      <dl className="divide-y divide-gray-200">
        {items.map((item, index) => (
          <div key={index} className="px-4 py-3 grid grid-cols-3 gap-4">
            <dt className="text-sm font-medium text-gray-500">{item.label}</dt>
            <dd className="text-sm text-gray-900 col-span-2">{item.value || '-'}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
